import React, { useEffect, useMemo, useState } from "react";
import TextFieldRow from "../../../../../components/TextFieldRow";
import TextField from "../../../../../components/TextField";

type Props = {
  numeroOS: string;
  setNumeroOS: (v: string) => void;

  nomeEmbarcacao: string;
  setNomeEmbarcacao: (v: string) => void;

  responsavelLiberacao: string; // (se não usar ainda, pode remover do tipo e do form)
  setResponsavelLiberacao: (v: string) => void;

  responsavelColeta: string;
  setResponsavelColeta: (v: string) => void;

  nomeEmpresa: string;
  setNomeEmpresa: (v: string) => void;
};

const EMPRESAS_URL =
  "https://api.eva-googlemarine.com/geral/empresa-clientes-nome";
const COLABS_URL =
  "https://api.eva-googlemarine.com/departamentos/recursos-humanos/colaboradores-nome";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const ServicoMovement: React.FC<Props> = ({
  numeroOS,
  setNumeroOS,
  nomeEmbarcacao,
  setNomeEmbarcacao,
  responsavelColeta,
  setResponsavelColeta,
  nomeEmpresa,
  setNomeEmpresa,
}) => {
  // --- Estados de Empresas
  const [empresaOptions, setEmpresaOptions] = useState<string[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [erroEmpresas, setErroEmpresas] = useState<string | null>(null);

  // --- Estados de Colaboradores (Responsável pela Coleta)
  const [colabOptions, setColabOptions] = useState<string[]>([]);
  const [loadingColabs, setLoadingColabs] = useState(false);
  const [erroColabs, setErroColabs] = useState<string | null>(null);

  // Normalizador: converte para MAIÚSCULO enquanto digita
  const toUpper = (fn: (v: string) => void) => (v: string) =>
    fn((v || "").toUpperCase());

  // --- Fluxo sequencial: Empresas -> (aguarda 1s) -> Colaboradores
  useEffect(() => {
    let alive = true;
    const ctrlEmpresas = new AbortController();
    const ctrlColabs = new AbortController();

    (async () => {
      // 1) EMPRESAS
      setLoadingEmpresas(true);
      setErroEmpresas(null);
      try {
        const res = await fetch(EMPRESAS_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: ctrlEmpresas.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list: string[] = Array.isArray(json?.data)
          ? (json.data as string[])
          : [];

        const unique = Array.from(
          new Set(
            list
              .map((s) => (typeof s === "string" ? s.trim().toUpperCase() : ""))
              .filter(Boolean)
          )
        );

        if (alive) setEmpresaOptions(unique);
      } catch (e: any) {
        // suprime abort
        if (e?.name !== "AbortError" && alive) {
          setErroEmpresas(String(e?.message || e));
        }
      } finally {
        if (alive) setLoadingEmpresas(false);
      }

      // 2) ESPERA 1s ANTES DO PRÓXIMO FETCH
      await sleep(1000);
      if (!alive) return;

      // 3) COLABORADORES
      setLoadingColabs(true);
      setErroColabs(null);
      try {
        const res = await fetch(COLABS_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: ctrlColabs.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        // Esperado: { data: [{ nome_completo: "Fulano" }, ...] }
        const list: string[] = Array.isArray(json?.data)
          ? (json.data as Array<{ nome_completo?: string }>).map((x) =>
              (x?.nome_completo ?? "").trim()
            )
          : [];

        const unique = Array.from(
          new Set(
            list
              .map((s) => (typeof s === "string" ? s.trim().toUpperCase() : ""))
              .filter(Boolean)
          )
        );

        if (alive) setColabOptions(unique);
      } catch (e: any) {
        if (e?.name !== "AbortError" && alive) {
          setErroColabs(String(e?.message || e));
        }
      } finally {
        if (alive) setLoadingColabs(false);
      }
    })();

    return () => {
      alive = false;
      ctrlEmpresas.abort();
      ctrlColabs.abort();
    };
  }, []);

  // Memo dropdowns
  const empresaDropdown = useMemo(
    () => (empresaOptions.length ? empresaOptions : undefined),
    [empresaOptions]
  );

  const colabDropdown = useMemo(
    () => (colabOptions.length ? colabOptions : undefined),
    [colabOptions]
  );

  return (
    <div>
      {/* RESPONSÁVEL PELA COLETA (DROPDOWN) */}
      <TextFieldRow
        center={{
          label: "Responsável pela Coleta",
          value: responsavelColeta,
          onChange: toUpper(setResponsavelColeta),
          placeholder: loadingColabs
            ? "CARREGANDO COLABORADORES..."
            : erroColabs
            ? "FALHA AO CARREGAR. DIGITE MANUALMENTE."
            : "SELECIONE O RESPONSÁVEL",
          options: colabDropdown,
        }}
      />

      {/* EMPRESA + EMBARCAÇÃO */}
      <TextFieldRow
        left={{
          label: "Empresa",
          value: nomeEmpresa,
          onChange: toUpper(setNomeEmpresa),
          placeholder: loadingEmpresas
            ? "CARREGANDO EMPRESAS..."
            : erroEmpresas
            ? "FALHA AO CARREGAR. DIGITE MANUALMENTE."
            : "SELECIONE A EMPRESA",
          options: empresaDropdown,
        }}
        right={{
          label: "Embarcação",
          value: nomeEmbarcacao,
          onChange: toUpper(setNomeEmbarcacao),
          placeholder: "Digite o nome da Embarcação",
        }}
      />

      {/* NÚMERO DA O.S */}
      <TextField
        label="Número da O.S"
        value={numeroOS}
        onChange={toUpper(setNumeroOS)}
        placeholder="Digite aqui o número da O.S"
        uppercase={true}
      />
    </div>
  );
};

export default ServicoMovement;
